// filepath: apps/web/src/features/lists/tree/TreeNode.tsx
//
// FR-UI-VIEW-2 AC-1 / AC-2 / AC-3 — recursive single-row renderer
// for the Tree view.
//
// A `TreeNode` renders one record as a horizontal row at a given
// depth. When the record has children and the user has toggled
// the row to "expanded", the component recursively renders its
// children at depth+1. The recursion is bounded by the server's
// `depth_cap` (validator-side: `MaxTreeDepthCap = 64`) — see
// internal/entity/view_config_validators.go.
//
// SECURITY / INTEGRITY INVARIANTS:
//   - INV-2 / FR-AUTH-7: the renderer iterates ONLY
//     `Object.keys(record.fields)`; never reads `definition.fields`
//     to invent a value for a missing key.
//   - INV-9: every record value is rendered as a text node; no
//     `dangerouslySetInnerHTML` path exists.
//   - ISS-303: every interactive element carries a stable
//     `data-testid` (chevron, row, indent guides).
//
// Design-token-only widths (ISS-306):
//   - chevron reserved slot: `var(--space-8)` (matches
//     design-system control height baseline).
//   - row vertical rhythm: `var(--space-2)` / `var(--space-3)`.
//   - card border: `var(--border-default)`.
//   - The one non-token number is `0.0625rem` -> `1px` for the
//     border; that is the design-system convention used by Card.

import { useMemo } from "react";
import { Badge, Button, Card } from "@/design-system";
import type { TreeRecord } from "./types";
import { IndentGuides } from "./IndentGuides";

/**
 * TreeNodeProps — single-row contract. Mirrors `TreeNodeProps` in
 * docs/design/FR-UI-VIEW-2/types.d.ts.
 */
export interface TreeNodeProps {
  /** The normalised record to render. */
  record: TreeRecord;
  /**
   * Zero-based depth of this row. Drives the number of indent
   * guides rendered to the left of the chevron. Bound to the
   * server-enforced `MaxTreeDepthCap` (64) — the renderer does
   * not truncate, since the validator already rejected anything
   * that could grow unbounded.
   */
  depth: number;
  /** Whether this node's subtree is currently expanded. */
  isExpanded: boolean;
  /** Children of this node (precomputed by `buildTreeIndex`). */
  children: ReadonlyArray<TreeRecord>;
  /** Whether this node is an orphan (parentValue -> absent id). */
  isOrphan: boolean;
  /** Whether this node is a leaf (no children, regardless of expand state). */
  isLeaf: boolean;
  /** Toggle handler (chevron click). */
  onToggle: (id: string) => void;
  /**
   * Recursive child renderer — supplied by TreeView so this
   * component stays decoupled from the recursive <TreeNode>
   * import (test-harness can swap a no-op recursion for
   * leaf-only tests).
   */
  renderChild: (child: TreeRecord, depth: number) => React.ReactNode;
  /**
   * Label for the record title — derived server-side as
   * `<fields>` (the placeholder copy). The renderer does NOT
   * pick the field key — the upstream hook picks it; the node
   * simply echoes the value when present.
   */
  titleLabel: string;
}

/**
 * TreeNode — single-row renderer.
 *
 * Layout (token-only):
 *   [ IndentGuides(d=depth) ] [ chevron | spacer ] [ Card { title, id, version, orphan tag } ]
 *
 * If `children.length > 0`, the chevron is a real `<Button>`
 * (ghost, size="sm"). If `children.length === 0`, the chevron
 * slot is a `var(--space-8)`-wide spacer (no Button, no click
 * target — prevents an empty click region from being a focus
 * trap).
 */
export function TreeNode({
  record,
  depth,
  isExpanded,
  children,
  isOrphan,
  isLeaf,
  onToggle,
  renderChild,
  titleLabel,
}: TreeNodeProps): React.ReactElement {
  const hasChildren = children.length > 0;

  // Stable memo for the indented body so the recursion does not
  // re-key every render. React keys are derived from record.id
  // at the parent (TreeView) — this memo is only for the inner
  // subtree slice.
  const subtree = useMemo(() => {
    if (!isExpanded || !hasChildren) return null;
    return children.map((child) => renderChild(child, depth + 1));
  }, [children, depth, hasChildren, isExpanded, renderChild]);

  const chevron = hasChildren ? (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={() => onToggle(record.id)}
      aria-expanded={isExpanded}
      aria-controls={`tree-node-subtree-${record.id}`}
      data-testid={`tree-node-chevron-${record.id}`}
      data-tree-chevron-state={isExpanded ? "expanded" : "collapsed"}
    >
      {isExpanded ? "v" : ">"}
    </Button>
  ) : (
    // Spacer width matches the chevron slot — token-only.
    <span
      aria-hidden="true"
      data-testid={`tree-node-leaf-spacer-${record.id}`}
      style={{
        display: "inline-flex",
        width: "var(--space-8)",
        height: "var(--space-8)",
      }}
    />
  );

  // The "row" is a horizontal flex of (guides, chevron, card).
  // The card is the row's content — id + version badge + the
  // server-rendered field values. We render `Object.keys(record
  // .fields)` only; the server is authoritative (INV-2).
  const fieldKeys = Object.keys(record.fields);

  return (
    <li
      data-testid={`tree-node-${record.id}`}
      data-tree-id={record.id}
      data-tree-depth={depth}
      data-tree-expanded={isExpanded ? "true" : "false"}
      data-tree-orphan={isOrphan ? "true" : "false"}
      data-tree-leaf={isLeaf ? "true" : "false"}
      style={{
        listStyle: "none",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-1)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-2)",
        }}
      >
        <IndentGuides depth={depth} />
        {chevron}
        <Card
          padding="sm"
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: "var(--space-3)",
          }}
        >
          <span
            style={{
              fontSize: "var(--text-md)",
              color: "var(--text-default)",
            }}
          >
            {/* Title is rendered as plain text only (INV-9). */}
            {titleLabel}
          </span>
          <span
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--text-muted)",
            }}
            data-testid={`tree-node-id-${record.id}`}
          >
            {record.id}
          </span>
          <Badge
            tone="neutral"
            data-testid={`tree-node-version-${record.id}`}
            style={{ marginLeft: "auto" }}
          >
            v{record.rowVersion}
          </Badge>
          {isOrphan ? (
            <Badge
              tone="warning"
              data-testid={`tree-node-orphan-badge-${record.id}`}
            >
              {"orphan"}
            </Badge>
          ) : null}
        </Card>
      </div>
      {/* Server-stripped field rendering (INV-2). Every key from
          record.fields is shown as a label/value text pair; the
          value is always coerced to a string and rendered as a
          text node (INV-9). */}
      {fieldKeys.length > 0 ? (
        <ul
          aria-label={"fields"}
          style={{
            listStyle: "none",
            margin: 0,
            paddingInlineStart: "var(--space-10)",
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--space-2)",
          }}
          data-testid={`tree-node-fields-${record.id}`}
        >
          {fieldKeys.map((k) => {
            const v = record.fields[k];
            const display = v === null || v === undefined ? "" : String(v);
            return (
              <li
                key={k}
                data-testid={`tree-node-field-${record.id}-${k}`}
                style={{
                  display: "inline-flex",
                  gap: "var(--space-1)",
                  fontSize: "var(--text-sm)",
                  color: "var(--text-muted)",
                }}
              >
                <span>{k}</span>
                <span aria-hidden="true">{"="}</span>
                <span style={{ color: "var(--text-default)" }}>{display}</span>
              </li>
            );
          })}
        </ul>
      ) : null}
      {/* Recursive subtree — only present when expanded + has children. */}
      {subtree ? (
        <ul
          id={`tree-node-subtree-${record.id}`}
          aria-label={"subtree"}
          data-testid={`tree-node-subtree-${record.id}`}
          style={{
            listStyle: "none",
            margin: 0,
            paddingInlineStart: 0,
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-1)",
          }}
        >
          {subtree}
        </ul>
      ) : null}
    </li>
  );
}
